import { Directive, ElementRef, Input, OnChanges, SimpleChanges, NgZone, OnDestroy, inject } from '@angular/core';

@Directive({
  selector: '[appCountUp]',
  standalone: false,
})
export class CountUpDirective implements OnChanges, OnDestroy {
  private el = inject(ElementRef);
  private ngZone = inject(NgZone);

  @Input('appCountUp') target = 0;
  @Input() countUpDuration = 600; // ms

  private currentValue = 0;
  private rafId: number | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['target']) {
      const to = this.target ?? 0;
      this.animate(this.currentValue, to);
    }
  }

  private animate(from: number, to: number): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (from === to) {
      this.el.nativeElement.textContent = String(to);
      return;
    }

    const start = performance.now();
    const duration = this.countUpDuration;
    const diff = to - from;

    // Run entirely outside Angular zone — DOM is updated directly, no CD triggered per frame
    this.ngZone.runOutsideAngular(() => {
      const step = (timestamp: number) => {
        const elapsed = timestamp - start;
        const progress = Math.min(elapsed / duration, 1);
        // Ease-out cubic: fast start, smooth landing
        const eased = 1 - Math.pow(1 - progress, 3);
        const value = Math.round(from + diff * eased);
        this.el.nativeElement.textContent = String(value);
        this.currentValue = value;

        if (progress < 1) {
          this.rafId = requestAnimationFrame(step);
        } else {
          this.currentValue = to;
          this.rafId = null;
        }
      };
      this.rafId = requestAnimationFrame(step);
    });
  }

  ngOnDestroy(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
    }
  }
}
